import React, {
	useState,
	useEffect,
	useCallback,
	useMemo,
	useDeferredValue,
	useRef
} from "react"
import Popup from "reactjs-popup"
import { useDispatch, useSelector } from "react-redux"
import { find } from "lodash"
import axios from "axios"
import { usePostHog } from "posthog-js/react"
import { RxDragHandleDots2 } from "react-icons/rx"
import { AiOutlineQuestionCircle } from "react-icons/ai"

import {
	updateSchedule,
	setOptimizeData,
	updatePatientInSchedule,
	setIsEditing,
	transferPatientsBetweenNursesAndChairs,
	updatePatientField,
	addPatientField
} from "../../features/schedule/scheduleSlice"
import {
	updateSandboxPatientInSchedule,
	transferPatientsBetweenNursesAndChairsSandbox
} from "../../features/schedule/sandboxSlice"
import {
	updateTomorrowPatientInSchedule,
	setTomorrowData,
	transferPatientsBetweenNursesAndChairsTomorrow,
	addTomorrowPatientField
} from "../../features/schedule/tomorrowSlice"
import DropdownCombobox from "../DailyCalendarFiles/CustomCalendar/FormElements/DropdownCombobox"
import { PiWarningCircleFill } from "react-icons/pi"
import { useAuth0 } from "@auth0/auth0-react"
import { useQueryClient } from "@tanstack/react-query"
import useFetchNotifications from "../../hooks/useFetchNotifications"
import store from "../../store"
import { startTimeAllCases, endTimeAllCases } from "../../utils/realTimeUtils"
import {
	calculateNurseAcuity,
	calculateNurseAvailability,
	calculateTotalAcuity,
	getSortedNursesForPatient
} from "../../utils/realTimeNurseUtils"
import {
	getDateFormatted,
	getFirstOpenDateToday,
	getFirstOpenDateTomorrow
} from "../../utils/generalUtils"
import { getScheduleFromMongo } from "../../utils/mongoUtils"
import { useSchedulePatch } from "../../hooks/useSchedulePatch"
import {
	convertMinutesToMilitaryTime,
	convertMinutesTo12HHMM,
	timeStringToMinutes
} from "../../utils/generalUtils"
import SubmitEditButton from "./SubmitEditButton"
import SwapPatients from "./SwapPatients"
import { IoIosSwap } from "react-icons/io"
import { hasPermission } from "../../utils/realTimeUtils"
import {
	getChairType,
	getChairAvailabilityForWindow
} from "../../utils/realTimeChairUtils"
import useLogging from "../../hooks/useLogging"
import useFetchSettings from "../../hooks/useFetchSettings"
/**
 * This component is used to edit a patient's assignment.
 * It is used in the CustomCalendar component.
 * @param {Object} props - The component props.
 * @param {Patient} props.patient - The patient row to edit. Required — wrap the render in a truthy guard at the call site.
 * @param {boolean} props.isOpen - Whether the popup is open.
 * @param {function} props.onClose - The function to call when the popup is closed.
 * @param {Schedule} props.schedule - The schedule to use.
 * @param {"today" | "tomorrow" | "sandbox"} props.scheduleSelection - The schedule selection to use.
 * @param {(Object) => void} props.setNotification - The function to call to set a notification.
 * @param {boolean} props.disableClose - Whether to disable the close without saving.
 */
const EditPatientPopupInner = ({
	patient,
	isOpen,
	onClose,
	schedule,
	scheduleSelection,
	permissions,
	setNotification,
	disableClose = false
}) => {
	const { user } = useAuth0()
	const dispatch = useDispatch()
	const queryClient = useQueryClient()
	const { notificationsData: notifications } = useFetchNotifications()
	const isEditingPatient = useSelector(state => state.schedule.isEditing)
	const posthog = usePostHog()
	const logging = useLogging()

	const sandbox = useMemo(() => scheduleSelection === "sandbox", [scheduleSelection])
	const isTomorrowMode = useMemo(() => scheduleSelection === "tomorrow", [scheduleSelection])

	const schedulePatch = useSchedulePatch({ isTomorrowMode, sandbox })

	const [isSaving, setIsSaving] = useState(false)

	const [isSwapOpen, setIsSwapOpen] = useState(false)

	const [isDropdownFocused, setIsDropdownFocused] = useState(false)

	useEffect(() => {
		logging.info("isDropdownFocused", {
			labels: {
				isDropdownFocused: isDropdownFocused.toString()
			}
		})
		// `logging` is a fresh object each render (useLogging returns inline object); adding it would re-run every render.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isDropdownFocused])

	// Regular close handler for escape/click-outside
	const handleClose = () => {
		if (!isSaving) {
			// Reset editing state
			dispatch(setIsEditing(false))
		}
		logging.info("EditPatient - handleClose", {
			patient
		})
		setValidationErrors({})
		setWarningMessages({})
		setModifiedFields({})
		setIsSwapOpen(false)
		onClose()
	}

	const chargeNurse = schedule.chargeNurse

	const { settingsData } = useFetchSettings()
	const maxAcuityForNurse = settingsData?.maxAcuityForNurse
	const maxAcuityForNurseAT = settingsData?.maxAcuityForNurseAtAnyTime
	const { getAccessTokenSilently } = useAuth0() // Auth0 hook

	// Resolve the patient row. The outer wrapper keys this component by
	// patient.patientId, so patient is guaranteed non-null and switching patients
	// remounts with a clean state slate.
	const patientData = find(schedule.patientSchedule, { patientId: patient.patientId }) ?? patient

	// Read-only display fields derive directly from patientData — no useState
	// needed since the user can't edit them here.
	const patientName = patientData.patientName ?? ""
	const patientMRN = patientData.patientMRN ?? ""
	const infusionType = patientData.infusionType ?? ""
	const treatmentStartTime =
		convertMinutesToMilitaryTime(startTimeAllCases(patientData, sandbox, isTomorrowMode)) ?? ""
	const treatmentEndTime =
		convertMinutesToMilitaryTime(endTimeAllCases(patientData, sandbox, isTomorrowMode)) ?? ""

	const initialChairId =
		find(schedule.chairSchedule, { chairId: patientData.assignedChair })?.chairId ?? "auto"
	const initialNurseId =
		find(schedule.nurseSchedule, { nurseId: patientData.assignedNurse })?.nurseId ?? "auto"

	const [patientAcuity, setPatientAcuity] = useState(patientData.acuity ?? "")
	// Defer the value we feed into the heavy nurse-availability re-score so
	// typing in the acuity input stays responsive — React holds off the
	// recompute until it has spare time.
	const deferredAcuity = useDeferredValue(patientAcuity)
	const [nurseAssigned, setNurseAssigned] = useState(initialNurseId)
	const [chairAssigned, setChairAssigned] = useState(initialChairId)
	const [patientComments, setPatientComments] = useState("")
	const [validationErrors, setValidationErrors] = useState(() => {
		const errors = {}
		if (!patient.assignedNurse) errors.nurseAssigned = "A nurse must be assigned."
		if (!patient.assignedChair) errors.chairAssigned = "A chair must be assigned."
		return errors
	})

	const testEnvironment = user.org_id === "org_lkg3WMhi3Q2l92iT"
	const centerOpen = settingsData?.centerOpen
	const centerClosedDates = settingsData?.center_closed_dates || []

	const nextDate = getDateFormatted(
		getFirstOpenDateTomorrow(centerOpen, testEnvironment, centerClosedDates)
	)
	const date = isTomorrowMode
		? nextDate
		: getDateFormatted(getFirstOpenDateToday(centerOpen, testEnvironment, centerClosedDates))

	// Function to check if any changes have been made
	const hasChanges = () =>
		patientAcuity !== patient.acuity ||
		nurseAssigned !== patient.assignedNurse ||
		chairAssigned !== patient.assignedChair

	const hasWriteSchedulesPermission = permissions && permissions.includes("write:schedules")
	const hasWriteChairsPermission = permissions && permissions.includes("write:chairs")
	const hasRequestPermission = permissions && permissions.includes("write:schedule-personal")

	const [modifiedFields, setModifiedFields] = useState({})

	// Thin wrapper that closes over the current schedule + the patient being
	// edited so callers can pass just the chair window.
	const calculateChairAvailability = useCallback(
		(chairs, patientStart, patientEnd) =>
			getChairAvailabilityForWindow(
				chairs,
				patientStart,
				patientEnd,
				schedule.patientSchedule,
				patientMRN,
				sandbox,
				isTomorrowMode
			),
		[schedule.patientSchedule, patientMRN, sandbox, isTomorrowMode]
	)

	// Pure derivation of the dropdowns. useMemo caches across renders so we
	// only re-score nurses/chairs when the patient row or the schedule changes
	// — typing in the acuity / comments inputs keeps the previous result.
	const { sortedNurses, sortedChairs } = useMemo(() => {
		const sortedNurses = getSortedNursesForPatient({
			nurseSchedule: schedule.nurseSchedule,
			patientSchedule: schedule.patientSchedule,
			chairSchedule: schedule.chairSchedule,
			patient: patientData,
			sandbox,
			isTomorrowMode,
			maxAcuityForNurse,
			maxAcuityForNurseAT,
			logging
		})

		const patientStart = startTimeAllCases(patientData, sandbox, isTomorrowMode)
		const patientEnd = endTimeAllCases(patientData, sandbox, isTomorrowMode)
		const chairAvailability = calculateChairAvailability(
			schedule.chairSchedule,
			patientStart,
			patientEnd
		)
		const sortedChairs = schedule.chairSchedule
			.map((chair, index) => ({ ...chair, isAvailable: chairAvailability[index] }))
			.sort((a, b) => b.isAvailable - a.isAvailable)

		return { sortedNurses, sortedChairs }
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		patientData,
		schedule.patientSchedule,
		schedule.nurseSchedule,
		schedule.chairSchedule,
		sandbox,
		isTomorrowMode,
		maxAcuityForNurse,
		maxAcuityForNurseAT,
		calculateChairAvailability
	])

	const checkNumber = value => !value || isNaN(value) || value < 1 || value > 5 || value % 1 !== 0

	const [warningMessages, setWarningMessages] = useState({})

	// check nurse acuity limit within bounds if patient aquired
	const checkAcuityLimit = selectedNurse => {
		const totalAcuity = calculateTotalAcuity(selectedNurse, schedule.patientSchedule)
		if (patient.assignedNurse === selectedNurse.nurseId) {
			return totalAcuity > maxAcuityForNurse
		}
		return totalAcuity + Number(patientAcuity) > maxAcuityForNurse
	}

	//returns true if the patient schedule cannot be accommodated by the nurse schedule
	const checkPatientInNurseShift = selectedNurse => {
		const nurseWithAcuity = calculateNurseAcuity(
			[selectedNurse],
			schedule.patientSchedule,
			startTimeAllCases(patient, sandbox, isTomorrowMode),
			endTimeAllCases(patient, sandbox, isTomorrowMode),
			sandbox,
			isTomorrowMode
		)
		return calculateNurseAvailability(
			{
				patientSchedule: schedule.patientSchedule,
				nurseSchedule: nurseWithAcuity,
				chairSchedule: schedule.chairSchedule
			},
			patient,
			{ MAFN: true, MAFNAT: true },
			sandbox,
			isTomorrowMode,
			maxAcuityForNurse,
			maxAcuityForNurseAT,
			logging
		)[0]
	}

	const handlePatientChange = (e, fieldName = null) => {
		if (!e) return
		posthog.capture("schedule-patient-change", { fieldName })

		const { name, value } = e.target ? e.target : { name: fieldName, value: e }

		// Comments are free-text on the request flow — no validation, no
		// rescoring. Short-circuit so each keystroke avoids the spread + the
		// full handler body.
		if (name === "patientComments") {
			setPatientComments(value)
			if (!isEditingPatient) dispatch(setIsEditing(true))
			return
		}

		// Initialize a new errors object to hold the current validation errors
		const newErrors = { ...validationErrors }
		const newWarnings = { ...warningMessages }

		// Check Acuity Limit
		if (name === "nurseAssigned") {
			setNurseAssigned(value)
			const selectedNurse = schedule.nurseSchedule.find(nurse => nurse.nurseId === value)
			if (!value || value === "auto") {
				logging.info(
					"EditPatientPopup - No nurse assigned in EditPatient handleFieldChange:",
					{
						value: value,
						component: "EditPatientPopup"
					}
				)
				newErrors.nurseAssigned = "A nurse must be assigned."
			} else {
				delete newErrors.nurseAssigned
			}

			if (checkAcuityLimit(selectedNurse)) {
				logging.info(
					"EditPatientPopup - Acuity limit exceeded in EditPatient handleFieldChange:",
					{
						value: value,
						component: "EditPatientPopup"
					}
				)
				newWarnings.nurseAssigned =
					"Assigning this patient will exceed the nurse's maximum acuity."
			} else if (!checkPatientInNurseShift(selectedNurse)) {
				logging.info(
					"EditPatientPopup - Patient visit is outside of nurse's shift in EditPatient handleFieldChange:",
					{
						value: value,
						component: "EditPatientPopup"
					}
				)
				newWarnings.nurseAssigned = "Patient visit is outside of nurse's shift."
			} else {
				delete newWarnings.nurseAssigned
			}
		}

		if (name === "chairAssigned") {
			setChairAssigned(value)
			const selectedChair = schedule.chairSchedule.find(chair => chair.chairId === value)

			// Check if the selected chair is available
			const isChairAvailable = calculateChairAvailability(
				[selectedChair],
				startTimeAllCases(patient, sandbox, isTomorrowMode),
				endTimeAllCases(patient, sandbox, isTomorrowMode)
			)[0]

			const overlappingPatient = !isChairAvailable
			if (!value || value === "auto") {
				logging.info(
					"EditPatientPopup - No chair assigned in EditPatient handleFieldChange:",
					{
						value: value,
						component: "EditPatientPopup"
					}
				)
				newErrors.chairAssigned = "A chair must be assigned."
			} else {
				delete newErrors.chairAssigned
			}

			if (overlappingPatient) {
				logging.info(
					"EditPatientPopup - Chair overlapping in EditPatient handleFieldChange:",
					{
						value: value,
						component: "EditPatientPopup"
					}
				)
				newWarnings.chairAssigned =
					"This chair will have more than one patient at the same time."
			} else {
				delete newWarnings.chairAssigned
			}
		}
		const safeValue = value === null ? "" : value

		if (name === "patientAcuity") {
			setPatientAcuity(safeValue)
			const selectedNurse = find(schedule.nurseSchedule, { nurseId: nurseAssigned })

			if (checkNumber(safeValue)) {
				logging.info(
					"EditPatientPopup - Entered acuity exceeds will push nurse past acuity limit",
					{ component: "EditPatientPopup" }
				)
				newErrors.patientAcuity = "Acuity must be a valid integer number."
			} else {
				delete newErrors.patientAcuity
			}

			if (checkAcuityLimit(selectedNurse)) {
				logging.info("A nurse's acuity limit exceeded in EditPatient handleFieldChange:", {
					value: safeValue
				})
				newWarnings.patientAcuity = "This acuity exceeds the nurse's maximum acuity."
			} else {
				delete newWarnings.patientAcuity
			}
		}

		setValidationErrors(newErrors)
		setWarningMessages(newWarnings)

		logging.info("EditPatient - handlePatientChange", {
			patient,
			name,
			value,
			component: "EditPatientPopup"
		})

		const updatedPatient = {
			...patient,
			acuity: name === "patientAcuity" ? safeValue : Number(patientAcuity),
			assignedNurse: name === "nurseAssigned" ? value : nurseAssigned,
			assignedChair: name === "chairAssigned" ? value : chairAssigned
		}

		// Use getChangedFields to determine modified fields
		setModifiedFields(getChangedFields(patient, updatedPatient))
		// Already-editing dispatch is a no-op for subscribers but still goes
		// through Redux — skip it when isEditing is already true.
		if (!isEditingPatient) dispatch(setIsEditing(true))
	}

	// Helper function to get changed fields between two objects
	const getChangedFields = (original, updated) => {
		const changes = {}

		Object.keys(original).forEach(key => {
			if (updated[key] !== original[key]) {
				changes[key] = {
					old: original[key],
					new: updated[key]
				}
			}
		})

		return changes
	}

	// Updated handleSave function
	const handleSave = async () => {
		// Set errors if any and stop execution
		if (Object.keys(validationErrors).length > 0) {
			return
		}

		logging.info("EditPatient - starting handleSave", {
			component: "EditPatientPopup",
			patient
		})

		setIsSaving(true)
		dispatch(setIsEditing(true))

		// Process selected nurse and chair
		const selectedNurse = find(schedule.nurseSchedule, { nurseId: nurseAssigned }) || {
			nurseName: "Unknown Nurse"
		}

		const selectedChair = find(schedule.chairSchedule, { chairId: chairAssigned }) || {
			chairName: "Unknown Chair"
		}

		const updatedPatient = {
			...patient,
			acuity: Number(patientAcuity),
			assignedNurse: selectedNurse.nurseId,
			assignedChair: selectedChair.chairId
		}

		onClose()

		// Get a fresh merged schedule from the backend (Pull)
		try {
			const token = await getAccessTokenSilently({
				audience: process.env.REACT_APP_API_IDENTIFIER
			})

			if (!sandbox) {
				const currentState = isTomorrowMode
					? store.getState().tomorrow
					: sandbox
						? store.getState().sandbox
						: store.getState().schedule

				await getScheduleFromMongo(
					token,
					date,
					dispatch,
					currentState,
					isTomorrowMode,
					sandbox,
					setTomorrowData,
					setOptimizeData,
					logging
				)
			}

			// If no fields have changed, do nothing
			if (Object.keys(modifiedFields).length === 0) {
				setNotification({
					visible: true,
					message: "No changes detected",
					success: false
				})
				setTimeout(() => {
					setNotification({ visible: false })
				}, 2000)
				dispatch(setIsEditing(false))
				setIsSaving(false)
				return
			}

			const currentNurse = find(schedule.nurseSchedule, { nurseEmail: user.email }) || {
				nurseId: null,
				nurseName: "Unknown Nurse"
			}

			let changesMade = false

			if (hasWriteSchedulesPermission || (sandbox && !isTomorrowMode)) {
				changesMade = true
				if (sandbox) {
					dispatch(
						transferPatientsBetweenNursesAndChairsSandbox([
							{
								newNurseId: selectedNurse.nurseId,
								newChairId: selectedChair.chairId,
								patientId: patient.patientId
							}
						])
					)
					if (updatedPatient.acuity !== patient.acuity) {
						dispatch(
							updateSandboxPatientInSchedule({
								updatedPatient
							})
						)
					}
				} else if (isTomorrowMode) {
					dispatch(
						transferPatientsBetweenNursesAndChairsTomorrow([
							{
								newNurseId: selectedNurse.nurseId,
								newChairId: selectedChair.chairId,
								patientId: patient.patientId
							}
						])
					)

					if (updatedPatient.acuity !== patient.acuity) {
						dispatch(
							updateTomorrowPatientInSchedule({
								updatedPatient
							})
						)

						dispatch(
							addTomorrowPatientField({
								patientId: patient.patientId,
								fieldName: "acuity"
							})
						)
					}
				} else {
					dispatch(
						transferPatientsBetweenNursesAndChairs([
							{
								newNurseId: selectedNurse.nurseId,
								newChairId: selectedChair.chairId,
								patientId: patient.patientId
							}
						])
					)

					if (updatedPatient.acuity !== patient.acuity) {
						dispatch(
							updatePatientInSchedule({
								updatedPatient
							})
						)

						dispatch(
							addPatientField({
								patientId: patient.patientId,
								fieldName: "acuity"
							})
						)
					}
				}

				if (!sandbox) {
					// Optimistic save: onMutate clears isEditing synchronously so
					// the UI unblocks immediately. The network call runs in the
					// background; onError will refetch to roll back if it fails.
					await schedulePatch.mutateAsync({ date, setNotification })
				}

				if (!isTomorrowMode && !sandbox) {
					// Send notification and update Redux state with latest notifications
					await sendNotification(
						patient.patientId,
						updatedPatient,
						"bedsideNurse"
					)
					await fetchNotifications() // Fetch updated notifications and update Redux state
				} else {
					logging.info(
						"EditPatientPopup - handle save - not sending notifications because is sandbox or tomorrow",
						{
							component: "EditPatientPopup",
							function: "handleSave",
							patient
						}
					)
				}

				dispatch(setIsEditing(false))
				setIsSaving(false)
			}

			if (
				!hasWriteSchedulesPermission &&
				!sandbox &&
				!isTomorrowMode &&
				hasWriteChairsPermission &&
				modifiedFields.assignedChair
			) {
				changesMade = true
				dispatch(
					transferPatientsBetweenNursesAndChairs([
						{
							newChairId: selectedChair.chairId,
							patientId: patient.patientId
						}
					])
				)

				// Optimistic save (see above)
				await schedulePatch.mutateAsync({ date, setNotification })

				if (patient.assignedNurse !== null && patient.assignedNurse !== currentNurse?.nurseId) {
					// This is preventing you from sending a notification to yourself
					// Send notification and update Redux state with latest notifications
					// NOTE: we only include the chair changes in the notification
					await sendNotification(
						patient.patientId,
						{ ...patient, assignedChair: updatedPatient.assignedChair },
						"bedsideNurse"
					)
					await fetchNotifications() // Fetch updated notifications and update Redux state
				}

				dispatch(setIsEditing(false))
				setIsSaving(false)
			}

			if (
				!hasWriteSchedulesPermission &&
				!sandbox &&
				!isTomorrowMode &&
				hasRequestPermission &&
				(modifiedFields.assignedNurse || modifiedFields.acuity)
			) {
				changesMade = true
				// User lacks permission: send approval request to Charge Nurse
				// Prepare a pending update object (this assumes your backend can handle it)
				// Identify the original patient data
				// Get only the changed fields

				// Check for existing pending notifications for the same patient
				const existingPendingNotification = notifications.find(
					notification =>
						notification.additionalData?.requestedBy?.sub === currentNurse?.nurseId && // Same user
						notification.additionalData?.patientId === patient.patientId && // Same patient
						!notification.decision // Pending notification
				)

				if (existingPendingNotification) {
					// Alert user that there's already a pending request
					setIsSaving(false)
					dispatch(setIsEditing(false))
					onClose()
					setNotification({
						visible: true,
						message:
							"There is already an outstanding request for this patient. Please withdraw the previous request or ask the charge nurse to review it.",
						success: false
					})
					setTimeout(() => {
						setNotification({ visible: false })
					}, 2000)
					return
				}

				// TODO: we can add another limit here about the number of notifications that can be sent if its needed.

				if (hasWriteChairsPermission && modifiedFields.assignedChair) {
					patient.assignedChair = updatedPatient.assignedChair
				}

				const pendingUpdate = {
					patientId: patient.patientId,
					proposedChanges: updatedPatient,
					requestedBy: user,
					requestedAt: new Date().toISOString()
				}

				try {
					// Send a notification to the Charge Nurse about the pending update
					await sendNotification(
						patient.patientId,
						updatedPatient,
						"chargeNurse",
						pendingUpdate,
						pendingUpdate
					)
					await fetchNotifications() // Fetch updated notifications and update Redux state

					// Provide feedback to the user
					setNotification({
						visible: true,
						message: "Changes submitted for CN review.",
						success: true
					})
					setTimeout(() => {
						setNotification({ visible: false })
					}, 2000)
					onClose()
					dispatch(setIsEditing(false))
					setIsSaving(false)
				} catch (error) {
					logging.error("EditPatientPopup - Error submitting pending update:", {
						error: error.message,
						stack: error.stack,
						component: "EditPatientPopup",
						function: "handleSave",
						patient
					})
					setNotification({
						visible: true,
						message: "There was an error submitting your changes. Please try again.",
						success: false
					})
					setTimeout(() => {
						setNotification({ visible: false })
					}, 2000)
				}
			}

			if (changesMade) {
				setNotification({ visible: true, message: "Changes saved.", success: true })
				setTimeout(() => {
					setNotification({ visible: false })
				}, 2000)
			} else {
				setNotification({ visible: true, message: "No changes made.", success: false })
				setTimeout(() => {
					setNotification({ visible: false })
				}, 2000)
			}

			dispatch(setIsEditing(false))
			setIsSaving(false)

			logging.info("EditPatient - handleSave completed", {
				component: "EditPatientPopup",
				patient
			})

			onClose()
		} catch (error) {
			logging.error("EditPatientPopup - Error fetching merged schedule:", {
				error: error.message,
				stack: error.stack
			})
		}
	}

	// Keep a live ref to handleSave so the Cmd/Ctrl+Enter listener always
	// calls the latest closure without re-binding on every render.
	const handleSaveRef = useRef(handleSave)
	handleSaveRef.current = handleSave

	function getChairName(chairId) {
		const chair = find(schedule.chairSchedule, { chairId: chairId })
		return chair ? chair.chairName : "Chair not found"
	}

	function getNurseName(nurseId) {
		const nurse = find(schedule.nurseSchedule, { nurseId: nurseId })
		return nurse ? nurse.nurseName : "Nurse not found"
	}

	const sendNotification = async (
		patientId,
		newPatient,
		recipientType = "chargeNurse",
		proposedChanges = null,
		additionalData = null
	) => {
		// Determine if the notification is for the charge nurse
		const isForChargeNurse = recipientType === "chargeNurse"

		const changedFields = getChangedFields(patient, newPatient)

		const patientName = newPatient.patientName || "Unknown Patient"

		// Build the markdown content based on the recipient
		let markdownContent = `
**Full Patient Details:**
- Patient Name: ${patientName}
- Infusion Type: ${proposedChanges?.infusionType || newPatient.infusionType}
${
	changedFields.acuity
		? `- Acuity: ${changedFields.acuity.old} ➔ ${changedFields.acuity.new}`
		: `- Acuity: ${proposedChanges?.acuity || newPatient.acuity}`
}
${
	changedFields.assignedNurse
		? `- Nurse: ${getNurseName(changedFields.assignedNurse.old)} ➔ ${getNurseName(
				changedFields.assignedNurse.new
			)}`
		: `- Nurse: ${getNurseName(proposedChanges?.assignedNurse || newPatient.assignedNurse)}`
}
${
	changedFields.assignedChair
		? `- Chair: ${getChairName(changedFields.assignedChair.old)} ➔ ${getChairName(
				changedFields.assignedChair.new
			)}`
		: `- Chair: ${getChairName(proposedChanges?.assignedChair || newPatient.assignedChair)}`
}
${
	changedFields.scheduledStartTime
		? `- Start Time: ${convertMinutesToMilitaryTime(
				changedFields.scheduledStartTime.old
			)} ➔ ${convertMinutesTo12HHMM(changedFields.scheduledStartTime.new)}`
		: `- Start Time: ${convertMinutesTo12HHMM(
				proposedChanges?.scheduledStartTime || newPatient.scheduledStartTime
			)}`
}
${
	changedFields.scheduledEndTime
		? `- End Time: ${convertMinutesToMilitaryTime(
				changedFields.scheduledEndTime.old
			)} ➔ ${convertMinutesTo12HHMM(changedFields.scheduledEndTime.new)}\n`
		: `- End Time: ${convertMinutesTo12HHMM(
				proposedChanges?.scheduledEndTime || newPatient.scheduledEndTime
			)}\n`
}
`

		if (isForChargeNurse) {
			if (patientComments !== "") {
				markdownContent += `\n${additionalData.requestedBy.name.split(" ")[0]}'s Comments:
${patientComments}
`
			}

			markdownContent += `
\nPlease review and approve or reject these changes.
`
		}

		// TODO: this is just sending the current value

		// Build the notification object
		const notificationData = {
			notification_name: isForChargeNurse
				? `Approval Needed: Changes requested for ${patientName} by ${
						additionalData.requestedBy.name.split(" ")[0]
					}`
				: `Updates for ${patientName} by ${chargeNurse.nurseName.split(" ")[0]}`,
			description: markdownContent,
			person_id: isForChargeNurse ? chargeNurse.nurseId : newPatient.assignedNurse,
			importance: "high",
			time: new Date().toISOString(),
			icon: isForChargeNurse ? "⚖️" : "🧑", // Different icons for different roles
			active: true,
			additionalData: additionalData || null, // Include the additional data if applicable (for charge nurse)
			decision: isForChargeNurse ? false : null,
			decisionValue: null
		}
		// Send the notification
		try {
			const token = await getAccessTokenSilently({
				audience: process.env.REACT_APP_API_IDENTIFIER
			})

			await axios.post(
				`${process.env.REACT_APP_BACKEND_URL}/api/notifications`,
				notificationData,
				{
					headers: { Authorization: `Bearer ${token}` }
				}
			)

			// If it's for the charge nurse, update the patient's notes
			if (isForChargeNurse) {
				let notesContent = `Awaiting approval on changes by charge nurse:\n`

				if (changedFields.acuity) {
					notesContent += `- Acuity: ${changedFields.acuity.old} ➔ ${changedFields.acuity.new}\n`
				}
				if (changedFields.assignedNurse) {
					notesContent += `- Nurse: ${getNurseName(
						changedFields.assignedNurse.old
					)} ➔ ${getNurseName(changedFields.assignedNurse.new)}\n`
				}
				if (changedFields.assignedChair) {
					notesContent += `- Chair: ${getChairName(
						changedFields.assignedChair.old
					)} ➔ ${getChairName(changedFields.assignedChair.new)}\n`
				}
				if (changedFields.scheduledStartTime) {
					notesContent += `- Start Time: ${convertMinutesTo12HHMM(
						changedFields.scheduledStartTime.old
					)} ➔ ${convertMinutesTo12HHMM(changedFields.scheduledStartTime.new)}\n`
				}
				if (changedFields.scheduledEndTime) {
					notesContent += `- End Time: ${convertMinutesTo12HHMM(
						changedFields.scheduledEndTime.old
					)} ➔ ${convertMinutesTo12HHMM(changedFields.scheduledEndTime.new)}\n`
				}

				if (patientComments !== "") {
					notesContent += `${additionalData.requestedBy.name.split(" ")[0]}'s Comments:
${patientComments}`
				}

				// Default notes if nothing specific changed
				if (notesContent === `Awaiting approval on changes by charge nurse:\n`) {
					notesContent = "Awaiting approval on changes by charge nurse"
				}

				const currentState = isTomorrowMode
					? store.getState().tomorrow
					: sandbox
						? store.getState().sandbox
						: store.getState().schedule

				const updatedPatient = {
					...patient,
					pendingChanges: true,
					notes: notesContent // Include the detailed notes
				}

				dispatch(
					updatePatientField({
						patientId: patient.patientId,
						fieldName: "pendingChanges"
					})
				)

				dispatch(
					updatePatientField({
						patientId: patient.patientId,
						fieldName: "notes"
					})
				)

				const updatedPatientSchedule = currentState.patientSchedule.map(p =>
					p.patientId === patientId ? updatedPatient : p
				)

				dispatch(updateSchedule({ patientSchedule: updatedPatientSchedule }))
			}
		} catch (error) {
			logging.warn("EditPatientPopup - Error sending approval or update notification:", {
				error: error.message,
				stack: error.stack,
				component: "EditPatientPopup",
				function: "sendNotification"
			})
			throw error
		}
	}

	const fetchNotifications = async () => {
		try {
			await queryClient.invalidateQueries({ queryKey: ["notifications"] })
		} catch (error) {
			logging.warn("EditPatientPopup - Error refreshing notifications:", {
				error: error.message,
				stack: error.stack,
				component: "EditPatientPopup",
				function: "fetchNotifications"
			})
		}
	}

	// Create the calculator with the current patient's data
	const nurseAvailabilityCalculator = useCallback(
		(nurses, currentTime, newPatientAcuity) => {
			return calculateNurseAvailability(
				{
					patientSchedule: schedule.patientSchedule,
					nurseSchedule: nurses,
					chairSchedule: schedule.chairSchedule
				},
				{
					...patient,
					acuity: newPatientAcuity
				},
				{ MAFN: true, MAFNAT: true },
				sandbox,
				isTomorrowMode,
				maxAcuityForNurse,
				maxAcuityForNurseAT,
				logging
			)
		},
		// Helper is stable within this component context.
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[patient, schedule, maxAcuityForNurseAT, sandbox, isTomorrowMode]
	)

	const chairAvailabilityCalculator = useCallback(
		(chairs, currentTime, newPatientAcuity) => {
			return calculateChairAvailability(
				chairs,
				startTimeAllCases(patient, sandbox, isTomorrowMode),
				endTimeAllCases(patient, sandbox, isTomorrowMode)
			)
		},
		// Helper is stable within this component context.
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[patient, sandbox, isTomorrowMode]
	)

	const [popupPosition, setPopupPosition] = useState({
		top: window.innerHeight / 2 - 300,
		left: window.innerWidth / 2 - 300
	})
	const [isDragging, setIsDragging] = useState(false)
	const [mainTooltipVisible, setMainTooltipVisible] = useState(false)
	const [chairTooltipVisible, setChairTooltipVisible] = useState(false)

	const handleMouseDown = e => {
		setIsDragging(true)
	}

	const handleMouseMove = e => {
		if (isDragging) {
			setPopupPosition({
				top: e.clientY - 35,
				left: e.clientX - 35
			})
		}
	}

	const handleMouseUp = () => {
		setIsDragging(false)
	}

	useEffect(() => {
		if (isDragging) {
			window.addEventListener("mousemove", handleMouseMove)
			window.addEventListener("mouseup", handleMouseUp)
		} else {
			window.removeEventListener("mousemove", handleMouseMove)
			window.removeEventListener("mouseup", handleMouseUp)
		}
		return () => {
			window.removeEventListener("mousemove", handleMouseMove)
			window.removeEventListener("mouseup", handleMouseUp)
		}
	}, [isDragging])

	// Cmd/Ctrl+Enter triggers save while the popup is open.
	useEffect(() => {
		if (!isOpen) return
		const onKeyDown = e => {
			if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
				e.preventDefault()
				handleSaveRef.current?.()
			}
		}
		window.addEventListener("keydown", onKeyDown)
		return () => window.removeEventListener("keydown", onKeyDown)
		// `handleSaveRef` is a ref and intentionally read on keydown without subscribing.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isOpen])

	// Add this function near the other handler functions
	const handleInputFocus = e => {
		e.target.select()
	}

	return (
		<Popup
			open={isOpen}
			onClose={handleClose}
			modal
			nested
			closeOnDocumentClick={!disableClose && !isDropdownFocused}
			closeOnEscape={!disableClose}
			contentStyle={{
				width: "80%",
				maxWidth: "600px",
				border: "none",
				borderRadius: "10px",
				padding: "0",
				position: "absolute",
				top: `${popupPosition.top}px`,
				left: `${popupPosition.left}px`
			}}
			overlayStyle={{ background: "rgba(0, 0, 0, 0.5)" }}
		>
			<div className={`bg-white p-6 rounded-xl shadow-md w-full mx-auto`}>
				<div className="flex items-center justify-between mb-4">
					<div className="flex items-center">
						<RxDragHandleDots2
							className={`text-gray-500 cursor-grab ${isDragging ? "cursor-grabbing" : ""}`}
							size={24}
							onMouseDown={handleMouseDown}
						/>
						<div className="text-xl font-semibold ml-2 text-custom-blue">
							Edit Patient
						</div>
					</div>
					<div className="relative">
						<AiOutlineQuestionCircle
							className="text-gray-500 cursor-pointer"
							size={24}
							onMouseEnter={() => setMainTooltipVisible(true)}
							onMouseLeave={() => setMainTooltipVisible(false)}
						/>
						{mainTooltipVisible && (
							<div className="absolute right-0 w-48 p-2 text-sm text-white bg-gray-800 rounded-md shadow-lg z-50">
								If you click and drag using the dots on the left-hand corner, you
								can move the dialogue while making edits. Once the chair is assigned
								in the tracking shell, you will not be able to edit the chair.
							</div>
						)}
					</div>
				</div>
				<form>
					{/* Patient Name (Display-Only) */}
					<div className="mb-4">
						<label className="block text-sm font-medium text-gray-700">
							Patient Name{" "}
						</label>
						<div className="relative mt-2 rounded-md shadow-sm">
							<input
								type="text"
								name="patientName"
								className={`block w-full rounded-md border-0 py-1.5 pr-10 
                                text-gray-900 placeholder:text-gray-500 
                                focus:ring-2 focus:ring-inset 
																sensitive
                                sm:text-sm sm:leading-6 ${
									validationErrors.patientName
										? "text-red-900 ring-1 ring-inset ring-red-300 placeholder:text-red-300 focus:ring-red-500"
										: "ring-1 ring-inset ring-gray-300 focus:ring-custom-blue"
								}`}
								placeholder="Enter patient name..."
								value={patientName}
								// onChange={e => handlePatientChange(e)}
								aria-invalid={validationErrors.patientName ? "true" : "false"}
								aria-describedby={
									validationErrors.patientName ? "patientName-error" : undefined
								}
								//change opacity to 0.5
								style={{ opacity: 0.5 }}
								disabled
							/>
							{validationErrors.patientName && (
								<div className="pointer-events-none absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center">
									<PiWarningCircleFill
										aria-hidden="true"
										className="h-5 w-5 text-red-500"
									/>
								</div>
							)}
						</div>
						{validationErrors.patientName && (
							<p id="patientName-error" className="mt-2 text-sm text-red-600">
								{validationErrors.patientName}
							</p>
						)}
					</div>

					{/* Patient Infusion Type (Display-Only) */}
					<div className="mb-4">
						<label className="block text-sm font-medium text-gray-700">
							Patient Infusion Type
						</label>
						<div className="relative mt-2 rounded-md shadow-sm">
							<input
								type="text"
								name="infusionType"
								className={`block w-full rounded-md border-0 py-1.5 pr-10 text-gray-900 placeholder:text-gray-500 focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6 ${
									validationErrors.infusionType
										? "text-red-900 ring-1 ring-inset ring-red-300 placeholder:text-red-300 focus:ring-red-500"
										: "ring-1 ring-inset ring-gray-300 focus:ring-custom-blue"
								}`}
								placeholder="Infusion type..."
								value={infusionType}
								// onChange={e => handlePatientChange(e)}
								aria-invalid={validationErrors.infusionType ? "true" : "false"}
								aria-describedby={
									validationErrors.infusionType ? "infusionType-error" : undefined
								}
								style={{ opacity: 0.5 }}
								disabled
							/>
							{validationErrors.infusionType && (
								<div className="pointer-events-none absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center">
									<PiWarningCircleFill
										aria-hidden="true"
										className="h-5 w-5 text-red-500"
									/>
								</div>
							)}
						</div>
						{validationErrors.infusionType && (
							<p id="infusionType-error" className="mt-2 text-sm text-red-600">
								{validationErrors.infusionType}
							</p>
						)}
					</div>

					{/* Treatment Start Time (Display-Only) */}
					<div className="mb-4">
						<div className="flex space-x-4">
							<div className="w-1/2">
								<label className="block text-sm font-medium text-gray-700">
									Treatment Start Time{" "}
								</label>
								<div className="relative mt-1">
									<input
										type="time"
										name="treatmentStartTime"
										className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-custom-blue focus:border-custom-blue sm:text-sm ${
											validationErrors.treatmentStartTime
												? "border-red-500"
												: ""
										}`}
										placeholder="HH:MM"
										value={treatmentStartTime}
										// onChange={e => handlePatientChange(e)}
										style={{ opacity: 0.5 }}
										disabled // Set as display-only
									/>
									{validationErrors.treatmentStartTime && (
										<p className="text-red-600 text-sm mt-1">
											{validationErrors.treatmentStartTime}
										</p>
									)}
								</div>
							</div>

							{/* Treatment End Time (Display-Only) */}
							<div className="w-1/2">
								<label className="block text-sm font-medium text-gray-700">
									Treatment End Time{" "}
								</label>
								<div className="relative mt-1">
									<input
										type="time"
										name="treatmentEndTime"
										className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-custom-blue focus:border-custom-blue sm:text-sm ${
											validationErrors.treatmentEndTime
												? "border-red-500"
												: ""
										}`}
										placeholder="HH:MM"
										value={treatmentEndTime}
										// onChange={e => handlePatientChange(e)}
										style={{ opacity: 0.5 }}
										disabled // Set as display-only
									/>
									{validationErrors.treatmentEndTime && (
										<p className="text-red-600 text-sm mt-1">
											{validationErrors.treatmentEndTime}
										</p>
									)}
								</div>
							</div>
						</div>
					</div>

					<div className="mb-4">
						<label className="block text-sm font-medium text-gray-700">
							Treatment Acuity <span className="text-red-500">*</span>
						</label>
						<div className="relative mt-2 rounded-md shadow-sm">
							<input
								type="number"
								name="patientAcuity"
								className={`block w-full rounded-md border-0 py-1.5 pr-10 text-gray-900 placeholder:text-gray-500 focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6 ${
									validationErrors.patientAcuity
										? "pr-10 text-red-900 ring-1 ring-inset ring-red-300 placeholder:text-red-300 focus:ring-red-500"
										: "ring-1 ring-inset ring-gray-300 focus:ring-custom-blue"
								}`}
								placeholder="Enter patient acuity..."
								value={patientAcuity ? patientAcuity : ""}
								onChange={e => handlePatientChange(e)}
								onFocus={handleInputFocus}
								disabled={!hasWriteSchedulesPermission && !hasRequestPermission}
								style={{
									opacity:
										!hasWriteSchedulesPermission && !hasRequestPermission
											? 0.5
											: 1
								}}
							/>

							{(validationErrors.patientAcuity || warningMessages.patientAcuity) && (
								<div className="pointer-events-none absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center">
									<PiWarningCircleFill
										aria-hidden="true"
										className="h-5 w-5 text-red-500"
									/>
								</div>
							)}
						</div>
						{validationErrors.patientAcuity && (
							<p id="patientAcuity-error" className="mt-2 text-sm text-red-600">
								{validationErrors.patientAcuity}
							</p>
						)}
						{warningMessages.patientAcuity && (
							<p id="patientAcuity-warning" className="mt-2 text-sm text-red-600">
								{warningMessages.patientAcuity}
							</p>
						)}
					</div>

					<div className="mb-4">
						<label className="block text-sm font-medium text-gray-700">
							Nurse Assigned <span className="text-red-500">*</span>
						</label>
						<div className="relative mt-2 rounded-md shadow-sm">
							<DropdownCombobox
								label="Enter Nurse Assigned..."
								options={sortedNurses}
								value={nurseAssigned || ""}
								name="nurseAssigned"
								style={{
									opacity:
										!hasWriteSchedulesPermission && !hasRequestPermission
											? 0.5
											: 1,
									color:
										!hasWriteSchedulesPermission && !hasRequestPermission
											? "gray"
											: "inherit"
								}}
								onChange={value => handlePatientChange(value, "nurseAssigned")}
								displayKey={nurse =>
									`${nurse.nurseName} (Acuity: ${nurse.currentAcuity})`
								}
								time={timeStringToMinutes(treatmentStartTime)}
								valueKey="nurseId"
								calculateAvailability={nurseAvailabilityCalculator}
								newPatientStart={startTimeAllCases(patient, sandbox, isTomorrowMode)}
								newPatientValue={deferredAcuity}
								newPatientMRN={patientMRN}
								maxAcuity={15}
								readOnly={!hasWriteSchedulesPermission && !hasRequestPermission}
								defaultValue={nurseAssigned || ""}
								className={`block w-full rounded-md border-0 py-1.5 pr-10 text-gray-900 placeholder:text-gray-500 focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6 ${
									validationErrors.nurseAssigned
										? "text-red-900 ring-1 ring-inset ring-red-300 placeholder:text-red-300 focus:ring-red-500"
										: "ring-1 ring-inset ring-gray-300 focus:ring-custom-blue"
								}`}
								aria-invalid={validationErrors.nurseAssigned ? "true" : "false"}
								aria-describedby={
									validationErrors.nurseAssigned
										? "nurseAssigned-error"
										: undefined
								}
								sandbox={sandbox}
								onFocus={() => {
									setIsDropdownFocused(true)
								}}
								onBlur={() => setIsDropdownFocused(false)}
								selectTextOnFocus={true}
							/>
							{(validationErrors.nurseAssigned || warningMessages.nurseAssigned) && (
								<div className="pointer-events-none absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center">
									<PiWarningCircleFill
										aria-hidden="true"
										className="h-5 w-5 text-red-500"
									/>
								</div>
							)}
						</div>
						{validationErrors.nurseAssigned && (
							<p id="nurseAssigned-error" className="mt-2 text-sm text-red-600">
								{validationErrors.nurseAssigned}
							</p>
						)}
						{warningMessages.nurseAssigned && (
							<p id="nurseAssigned-warning" className="mt-2 text-sm text-red-600">
								{warningMessages.nurseAssigned}
							</p>
						)}
					</div>

					<div className="mb-4">
						<label className="block text-sm font-medium text-gray-700 flex items-center">
							Chair Assigned <span className="text-red-500">*</span>
							{patient.location !== "" && (
								<div className="relative ml-1">
									<AiOutlineQuestionCircle
										className="text-gray-500 cursor-pointer"
										size={16}
										onMouseEnter={() => setChairTooltipVisible(true)}
										onMouseLeave={() => setChairTooltipVisible(false)}
									/>

									{chairTooltipVisible && (
										<div className="absolute left-0 w-48 p-2 text-sm text-white bg-gray-800 rounded-md shadow-lg z-50">
											This field is read-only because the location is set from
											the tracking shell.
										</div>
									)}
								</div>
							)}
						</label>
						<div className="relative mt-2 rounded-md shadow-sm">
							<DropdownCombobox
								label="Enter Chair Assigned..."
								options={sortedChairs}
								value={chairAssigned}
								name="chairAssigned"
								onChange={value => handlePatientChange(value, "chairAssigned")}
								displayKey={chair => `${chair.chairName} (${getChairType(chair)})`}
								time={timeStringToMinutes(treatmentStartTime)}
								valueKey="chairId"
								calculateAvailability={chairAvailabilityCalculator}
								newPatientStart={startTimeAllCases(patient, sandbox, isTomorrowMode)}
								newPatientValue={endTimeAllCases(patient, sandbox, isTomorrowMode)}
								readOnly={
									!hasWriteChairsPermission ||
									(!isTomorrowMode &&
										patient.location !== "" &&
										patient.assignedChair !== "" &&
										patient.assignedChair !== null &&
										patient.assignedChair !== undefined)
								}
								defaultValue={chairAssigned}
								shouldDisplayUpward={true}
								className={`block w-full rounded-md border-0 py-1.5 pr-10 text-gray-900 placeholder:text-gray-500 focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6 ${
									validationErrors.chairAssigned
										? "text-red-900 ring-1 ring-inset ring-red-300 placeholder:text-red-300 focus:ring-red-500"
										: "ring-1 ring-inset ring-gray-300 focus:ring-custom-blue"
								}`}
								aria-invalid={validationErrors.chairAssigned ? "true" : "false"}
								aria-describedby={
									validationErrors.chairAssigned
										? "chairAssigned-error"
										: undefined
								}
								sandbox={sandbox}
								style={{ opacity: !hasWriteChairsPermission ? 0.5 : 1 }}
								onFocus={() => {
									setIsDropdownFocused(true)
								}}
								onBlur={() => setIsDropdownFocused(false)}
								selectTextOnFocus={true}
							/>
							{(validationErrors.chairAssigned || warningMessages.chairAssigned) && (
								<div className="pointer-events-none absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center">
									<PiWarningCircleFill
										aria-hidden="true"
										className="h-5 w-5 text-red-500"
									/>
								</div>
							)}
						</div>
						{validationErrors.chairAssigned && (
							<p id="chairAssigned-error" className="mt-2 text-sm text-red-600">
								{validationErrors.chairAssigned}
							</p>
						)}
						{warningMessages.chairAssigned && (
							<p id="chairAssigned-warning" className="mt-2 text-sm text-red-600">
								{warningMessages.chairAssigned}
							</p>
						)}
					</div>

					{!hasWriteSchedulesPermission &&
						hasRequestPermission &&
						!sandbox &&
						!isTomorrowMode && (
							<div className="mb-4">
								<label className="block text-sm font-medium text-gray-700">
									Additional Comments for Charge Nurse
								</label>
								<div className="relative mt-2 rounded-md shadow-sm">
									<input
										type="text"
										name="patientComments"
										className="block w-full rounded-md border-0 py-1.5 pr-10 mt-4 text-gray-900 placeholder:text-gray-500 focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6 ring-1 ring-inset ring-gray-300 focus:ring-custom-blue"
										placeholder="Add reason for change request..."
										value={patientComments}
										onChange={e => handlePatientChange(e)}
										aria-invalid={false}
									/>
								</div>
							</div>
						)}
				</form>
				<div className="flex justify-between items-center mt-4">
					{/* <button onClick={handleDelete} className="text-red-500 text-sm font-semibold flex items-center">
                        <FaTrashAlt className="mr-2" /> Delete Patient
                    </button> */}

					{(hasPermission(permissions, "write:schedules") || sandbox) && (
						<button
							className="inline-flex items-center gap-1 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none"
							onClick={() => setIsSwapOpen(true)}
						>
							Swap Patients <IoIosSwap className="h-4 w-4 text-gray-600" />
						</button>
					)}

					<SubmitEditButton
						hasWriteSchedulesPermission={hasWriteSchedulesPermission}
						hasWriteChairsPermission={hasWriteChairsPermission}
						hasRequestPermission={hasRequestPermission}
						isTomorrowMode={isTomorrowMode}
						isEditingPatient={isEditingPatient}
						handleSave={handleSave}
						modifiedFields={modifiedFields}
						sandbox={sandbox}
						hasChanges={hasChanges}
						validationErrors={validationErrors}
					/>
				</div>

				<SwapPatients
					selectedPatient={patient}
					setNotification={setNotification}
					isOpen={isSwapOpen}
					onClose={() => {
						setIsSwapOpen(false)
					}}
					patientSchedule={schedule.patientSchedule}
					nurseSchedule={schedule.nurseSchedule}
					closePopup={() => handleClose()}
					logging={logging}
				/>
			</div>
		</Popup>
	)
}

// Re-mount on patient change so all useState inside re-initializes from props
// without a sync effect. Callers must only render this when patient is set.
const EditPatientPopup = (props) => (
	<EditPatientPopupInner key={props.patient.patientId} {...props} />
)

export default EditPatientPopup
